import { axiosV2 } from "config/@axios";
import storage from "@storage";
import { selectCanShare } from "./sharingSelectors";

export const FETCHING_GRANTEES = "sharingActions::fetchingGrantees";
export const FETCH_GRANTEES_SUCCESS = "sharingActions::fetchGranteesSuccess";
export const FETCH_GRANTEES_FAILED = "sharingActions::fetchGranteesFailed";

export const FETCHING_INVITATIONS = "sharingActions::fetchingInvitations";
export const FETCH_INVITATIONS_SUCCESS =
  "sharingActions::fetchInvitationsSuccess";
export const FETCH_INVITATIONS_FAILED =
  "sharingActions::fetchInvitationsFailed";

export const ADD_GRANTEE_SUCCESS = "sharingActions::addGranteeSuccess";
export const REMOVE_GRANTEE_SUCCESS = "sharingActions::removeGranteeSuccess";

export const FETCHING_RESOURCES = "sharingActions::fetchingResources";
export const FETCH_RESOURCES_SUCCESS = "sharingActions::fetchResourcesSuccess";
export const FETCH_RESOURCES_FAILED = "sharingActions::fetchResourcesFailed";
export const RESOURCES_CHANGED = "sharingActions::resourcesChanged";

export const FETCHING_SHARES = "sharingActions::fetchingShares";
export const FETCH_SHARES_SUCCESS = "sharingActions::fetchSharesSuccess";
export const FETCH_SHARES_FAILED = "sharingActions::fetchSharesFailed";
export const REMOVE_SHARE_SUCCESS = "sharingActions::removeShareSuccess";

export const OPEN_SHARE_MODAL = "sharingActions::openShareModal";
export const CLOSE_SHARE_MODAL = "sharingActions::closeShareModal";

export const ACCEPT_INVITATION_SUCCESS =
  "sharingActions::acceptInvitationSuccess";
export const SET_IGNORED_INVITATIONS = "sharingActions::setIgnoredInvitations";

// Without the sharing permission these answer 403; nothing is shared then
const withoutPermission = (getState, dispatch, type) => {
  if (selectCanShare(getState())) {
    return false;
  }

  dispatch({ type, payload: [] });
  return true;
};

export const fetchGrantees = () => (dispatch, getState) => {
  if (withoutPermission(getState, dispatch, FETCH_GRANTEES_SUCCESS)) {
    return Promise.resolve();
  }

  dispatch({ type: FETCHING_GRANTEES });

  return axiosV2
    .get("folders/_grantees", { params: { view: "all" } })
    .then(response => {
      dispatch({
        type: FETCH_GRANTEES_SUCCESS,
        payload: Array.isArray(response.data) ? response.data : []
      });
    })
    .catch(err => {
      dispatch({
        type: FETCH_GRANTEES_FAILED,
        payload: err.errorMessage
      });
    });
};

export const invitationKey = invitation =>
  `${invitation.zid}:${invitation.folder_id}`;

// Zimbra can't decline a share, so "Yok Say" only hides the invitation in
// this browser, per account
const ignoredStorageKey = email => `sharing_ignored_invitations:${email}`;

export const fetchInvitations = () => (dispatch, getState) => {
  if (withoutPermission(getState, dispatch, FETCH_INVITATIONS_SUCCESS)) {
    return Promise.resolve();
  }

  dispatch({ type: FETCHING_INVITATIONS });

  return axiosV2
    .get("folders/_invitations")
    .then(response => {
      const items = Array.isArray(response.data) ? response.data : [];
      const email = getState()?.auth?.user?.email;
      const storageKey = ignoredStorageKey(email);
      // Forget hidden invitations that are no longer pending
      const ignored = (storage.get(storageKey) || []).filter(key =>
        items.some(item => invitationKey(item) === key)
      );

      storage.set(storageKey, ignored);
      dispatch({ type: SET_IGNORED_INVITATIONS, payload: ignored });
      dispatch({ type: FETCH_INVITATIONS_SUCCESS, payload: items });
    })
    .catch(err => {
      dispatch({
        type: FETCH_INVITATIONS_FAILED,
        payload: err.errorMessage
      });
    });
};

export const addGrantee =
  ({ folder, email, type, role, notification, notificationMessage }) =>
  dispatch =>
    axiosV2
      .post(`folders/${folder.id}/grantees`, {
        email,
        type,
        ...(type !== "external" ? { role } : {}),
        notification: notification ? 1 : 0,
        ...(notification && notificationMessage
          ? { notification_message: notificationMessage }
          : {})
      })
      .then(response => {
        dispatch({
          type: ADD_GRANTEE_SUCCESS,
          payload: { folder, grantee: response.data }
        });

        return response.data;
      });

export const removeGrantee =
  ({ folderId, zid }) =>
  dispatch =>
    axiosV2
      .delete(`folders/${folderId}/grantees/${encodeURIComponent(zid)}`, {
        params: { notification: 0 }
      })
      .then(() => {
        dispatch({
          type: REMOVE_GRANTEE_SUCCESS,
          payload: { folderId, zid }
        });
      });

export const resendInvitation =
  ({ folderId, zid, message }) =>
  () =>
    axiosV2.put(
      `folders/${folderId}/grantees/${encodeURIComponent(zid)}/_send`,
      {
        action: "grant",
        ...(message ? { notification_message: message } : {})
      }
    );

// POST with the same address and a different role updates the grant
export const updateGrantee = ({ folder, grantee, role }) =>
  addGrantee({
    folder,
    email: grantee.email,
    type: grantee.type,
    role,
    notification: false
  });

export const fetchResources = view => (dispatch, getState) => {
  const current = getState().settings.sharing.resources[view];

  if (current?.isLoading || current?.isLoaded) {
    return Promise.resolve();
  }

  dispatch({ type: FETCHING_RESOURCES, payload: { view } });

  return axiosV2
    .get("folders", { params: { search_type: "tree", view } })
    .then(response => {
      dispatch({
        type: FETCH_RESOURCES_SUCCESS,
        payload: {
          view,
          items: Array.isArray(response.data) ? response.data : []
        }
      });
    })
    .catch(err => {
      dispatch({
        type: FETCH_RESOURCES_FAILED,
        payload: { view, errorMessage: err.errorMessage }
      });
    });
};

// After a calendar or task folder is added, renamed, recoloured or deleted
export const reloadResources = view => dispatch => {
  dispatch({ type: RESOURCES_CHANGED, payload: { view } });
  return dispatch(fetchResources(view));
};

// Folders other people shared with the user and the user accepted (mounted)
export const fetchShares = () => (dispatch, getState) => {
  // Sidebars and the mail list may ask at the same time
  if (getState()?.settings?.sharing?.shares?.isLoading) {
    return Promise.resolve();
  }

  if (withoutPermission(getState, dispatch, FETCH_SHARES_SUCCESS)) {
    return Promise.resolve();
  }

  dispatch({ type: FETCHING_SHARES });

  return axiosV2
    .get("folders/_shares", { params: { view: "all" } })
    .then(response => {
      dispatch({
        type: FETCH_SHARES_SUCCESS,
        payload: Array.isArray(response.data) ? response.data : []
      });
    })
    .catch(err => {
      dispatch({
        type: FETCH_SHARES_FAILED,
        payload: err.errorMessage
      });
    });
};

// Removing a share only deletes the mountpoint from the user's own mailbox
export const removeShare = share => dispatch =>
  axiosV2
    .delete(`folders/${share.id}`, { params: { delete_type: "delete" } })
    .then(() => {
      dispatch({ type: REMOVE_SHARE_SUCCESS, payload: share });
      dispatch(fetchResources(share.view));
    });

export const ignoreInvitation = invitation => (dispatch, getState) => {
  const storageKey = ignoredStorageKey(getState()?.auth?.user?.email);
  const ignored = [
    ...new Set([
      ...(getState().settings.sharing.invitations.ignored || []),
      invitationKey(invitation)
    ])
  ];

  storage.set(storageKey, ignored);
  dispatch({ type: SET_IGNORED_INVITATIONS, payload: ignored });
};

// Mounts the shared folder in the user's mailbox under the given name
export const acceptInvitation = (invitation, name, color) => dispatch =>
  axiosV2
    .put(
      `folders/${invitation.folder_id}/grantees/${encodeURIComponent(
        invitation.zid
      )}/_accept`,
      { name, ...(color ? { color } : {}) }
    )
    .then(() => {
      dispatch({ type: ACCEPT_INVITATION_SUCCESS, payload: invitation });
      dispatch(fetchResources(invitation.view));
      return dispatch(fetchShares());
    });

export const openShareModal = folder => ({
  type: OPEN_SHARE_MODAL,
  payload: { folder }
});

export const closeShareModal = () => ({ type: CLOSE_SHARE_MODAL });
